<template>
  <div class="companions-data">
    <div class="companions-data__container">
      <h3 class="user-data__title">
        <FontAwesomeIcon :icon="['far', 'users']" />

        {{
          isFree
            ? $t('landingpage.checkout-page.free.companions.title')
            : $t('landingpage.checkout-page.companions.title')
        }}
      </h3>

      <div class="user-data__intro">
        <p>
          {{
            allTicketsSoldOut
              ? $t(
                  'landingpage.checkout-page.companions.intro-all-tickets-sold-out',
                )
              : $t('landingpage.checkout-page.companions.intro')
          }}
        </p>
      </div>

      <CreateCompanion
        v-if="isCreateCompanionShown"
        :fields="fields"
        :companion-id="newCompanionId"
        @is-loading="(loading) => emit('is-loading', loading)"
        @success="companionSuccess"
        @expired-session="emit('expired-session')"
        @error="(err) => emit('error', err)"
        @remove-and-close="
          (id) => {
            if (id) removeCompanion({ id: id })
            isCreateCompanionShown = false
            emit('form-active', false)
          }
        "
        @close="
          () => {
            isCreateCompanionShown = false
            emit('form-active', false)
          }
        "
      />

      <div v-else class="companions-data__list">
        <div class="companions-data__list-items">
          <template
            v-for="(companion, index) in companions"
            :key="companion.id"
          >
            <div class="companions-data__list-item-wrapper">
              <div class="companions-data__list-item">
                <div class="companions-data__list-item-number">
                  {{ index + 2 }}
                </div>

                <div class="companions-data__list-item-details">
                  <p>
                    {{
                      companion?.contact?.first_name
                        ? `${companion.contact?.first_name} ${companion.contact?.last_name || ''}`
                        : $t(
                            'landingpage.checkout-page.confirmation.companion',
                            {
                              num: index + 1,
                            },
                          )
                    }}
                  </p>
                  <p>
                    {{
                      orderType === 'add-ons'
                        ? companion.add_ons?.length
                          ? companion.add_ons?.length > 1
                            ? $t(
                                'landingpage.checkout-page.number_of_add_ons',
                                {
                                  num: companion.add_ons?.length,
                                },
                              )
                            : $t('landingpage.checkout-page.one_add_on')
                          : ''
                        : companion.ticket
                          ? companionTicket(companion)
                          : ''
                    }}
                  </p>
                </div>

                <div class="companions-data__list-item-actions">
                  <button
                    type="button"
                    @click="attemptRemoveCompanion(companion)"
                  >
                    <FontAwesomeIcon :icon="['far', 'trash']" />
                  </button>

                  <button
                    type="button"
                    @click="
                      toggleUpdateCompanionCollapse(
                        index,
                        !companion.isUpdateShown,
                      )
                    "
                  >
                    <FontAwesomeIcon
                      :icon="[
                        'far',
                        companion.isUpdateShown ? 'chevron-up' : 'chevron-down',
                      ]"
                      class="text-gray-400"
                    />
                  </button>
                </div>
              </div>

              <UpdateCompanion
                v-if="companion.isUpdateShown"
                :fields="fields"
                :model="companion"
                @is-loading="(loading) => emit('is-loading', loading)"
                @success="companionSuccess"
                @tickets-addons-success="emit('success')"
                @error="(err) => emit('error', err)"
                @expired-session="emit('expired-session')"
              />
            </div>
          </template>
        </div>

        <button
          v-if="!allTicketsSoldOut"
          type="button"
          class="companions-data__btn-add btn"
          :disabled="isCompanionLimitReached"
          :aria-disabled="isCompanionLimitReached"
          :title="
            isCompanionLimitReached
              ? $t('landingpage.checkout-page.companions.limit-reached')
              : undefined
          "
          @click="!isCompanionLimitReached && createNewCompanion()"
        >
          <FontAwesomeIcon :icon="['far', 'plus']" />

          {{ $t('landingpage.checkout-page.companions.add-companion') }}
        </button>

        <p v-if="isCompanionLimitReached" class="companions-data__limit-info">
          {{ $t('landingpage.checkout-page.companions.limit-reached') }}
        </p>

        <p v-if="hasIncompleteCompanions" class="mt-3 text-sm text-red-600">
          {{ $t('landingpage.checkout-page.companions.incomplete-warning') }}
        </p>
      </div>
    </div>

    <Alert
      v-if="isConfirmDeleteCompanionShown"
      show
      :show-alert-icon="false"
      :title="$t('landingpage.checkout-page.companion.delete.title')"
      :message="$t('landingpage.checkout-page.companion.delete.message')"
      wrapper-class="companion-delete-alert"
      body-class="py-8! px-4!"
    >
      <template #buttons>
        <div class="flex items-center justify-center gap-2 pt-2">
          <button
            type="button"
            class="btn btn-cancel"
            @click="isConfirmDeleteCompanionShown = false"
            v-text="$t('common.cancel')"
          />
          <button
            type="button"
            class="btn btn-primary btn-confirm"
            @click="removeCompanion(selectedDeleteCompanion)"
            v-text="$t('common.confirm')"
          />
        </div>
      </template>
    </Alert>
  </div>
</template>

<script setup>
import { ref, inject, watch, computed } from 'vue'
import CreateCompanion from '@/Components/LandingPage/Checkout/CreateCompanion.vue'
import UpdateCompanion from '@/Components/LandingPage/Checkout/UpdateCompanion.vue'
import { formatCurrencyLeft } from '@/composables/currency'
import { useFieldPathResolver } from '@landingpage/Registration/composables/useFieldPathResolver'
import { getNested, dataSet } from '@landingpage/Common/utils'
import { useFetch } from '@/LandingPage/Checkout/useCheckoutFetch'
import Alert from '@/Components/Alert.vue'

const props = defineProps({
  fields: { type: [Array, Object], default: () => ({}) },
})

const emit = defineEmits([
  'form-active',
  'success',
  'error',
  'is-loading',
  'expired-session',
])

const isCreateCompanionShown = ref(false)
const isConfirmDeleteCompanionShown = ref(false)
const selectedDeleteCompanion = ref(null)
const isFetching = ref(true)
const selectedCompanionIndex = ref(null)
const newCompanionId = ref(null)
const companions = ref([])

const orderDetails = inject('orderDetails')
const companionGuests = inject('companionGuests')
const mainGuest = inject('mainGuest')
const isFree = inject('isFree')
const activeLocale = inject('activeLocale')
const orderType = inject('orderType')
const eventId = inject('eventId')
const orderId = inject('orderId')
const tickets = inject('tickets')

const allTicketsSoldOut = computed(
  () =>
    orderType.value !== 'add-ons' &&
    tickets.value.length > 0 &&
    tickets.value.every((t) => t.remaining <= 0),
)

const isCompanionLimitReached = computed(() => {
  const limit = mainGuest.value?.max_companion_limit
  if (limit === null || limit === undefined) return false
  return companions.value.length >= limit
})

const hasIncompleteCompanions = inject('hasIncompleteCompanions')

const { getFieldPath } = useFieldPathResolver()

const generateGuestFields = (guest, fields) => {
  let initialData = {
    contact: {},
    extended_fields: {},
    booking: { extended_fields: {} },
  }

  if (fields) {
    fields.forEach((field) => {
      const path = getFieldPath(field)
      if (path) {
        const value = getNested(guest, path) ?? null
        dataSet(initialData, path, value)
      }
    })
  }

  return initialData
}

const getCompanionsData = () => {
  emit('is-loading', true)
  isFetching.value = true
  companions.value = companionGuests.value.map((guest) => {
    return {
      id: guest.id,
      ticket: guest.items?.find(({ product }) => product.type === 'ticket'),
      ticket_details: guest.items?.find(
        ({ product }) => product.type === 'ticket',
      ),
      add_ons: guest.items?.filter(({ product }) => product.type === 'addon'),
      ...generateGuestFields(guest, props.fields.items),
    }
  })
  if (selectedCompanionIndex.value !== null && companions.value?.length) {
    companions.value[selectedCompanionIndex.value].isUpdateShown = true
  }

  isFetching.value = false
  emit('is-loading', false)
}

const companionTicket = (companion) => {
  const ticketDetails = orderDetails.value?.line_items.find(
    ({ option_from, option_to, name }) => {
      return (
        companion?.ticket_details.starts_at === option_from &&
        companion?.ticket_details.ends_at === option_to &&
        name[activeLocale] ===
          companion?.ticket_details?.product?.name[activeLocale]
      )
    },
  )

  if (isFree.value) {
    return `${ticketDetails?.name[activeLocale]}`
  } else {
    return `${ticketDetails?.name[activeLocale]} • ${formatCurrencyLeft(ticketDetails?.total_gross_price || 0, {}, orderDetails.value?.currency)}`
  }
}

const companionSuccess = () => {
  isCreateCompanionShown.value = false
  emit('form-active', false)
  emit('success')
}

const createNewCompanion = () => {
  selectedCompanionIndex.value = null
  newCompanionId.value = null
  emit('form-active', true)
  isCreateCompanionShown.value = true
}

const attemptRemoveCompanion = (guest) => {
  selectedDeleteCompanion.value = guest
  isConfirmDeleteCompanionShown.value = true
}

// Remove companion Guest
const removeCompanion = async (guest) => {
  emit('is-loading', true)
  isConfirmDeleteCompanionShown.value = false
  const { statusCode } = await useFetch(
    route('api.checkout.guest.destroy', {
      event: eventId,
      order: orderId.value,
      guest: guest?.id,
    }),
  ).delete()
  if (statusCode.value && statusCode.value === 404) {
    emit('expired-session')
    return
  }

  selectedCompanionIndex.value = null
  selectedDeleteCompanion.value = null
  companionSuccess()
}

const toggleUpdateCompanionCollapse = (index, show) => {
  selectedCompanionIndex.value = show ? index : null
  companions.value.forEach((guest) => (guest.isUpdateShown = false))
  companions.value[index].isUpdateShown = show
}

getCompanionsData()

watch(
  () => companionGuests.value,
  () => getCompanionsData(),
  { deep: true },
)
</script>
